import type { ExtractedData, ExtractedLine, MyInvoiceStatus } from '@/domain/myInvoice/MyInvoice'

/** Monotonic client-side id for freshly added review lines (no backend id yet). */
let lineSeq = 0
export function newLineId(): string {
  lineSeq += 1
  return `l-${lineSeq}`
}

export function buildEmptyExtractedLine(): ExtractedLine {
  return { id: newLineId(), description: '', quantity: 1, unitPrice: 0, total: 0 }
}

/** quantity × unitPrice, rounded to the currency's decimals (whole units only for CLP-style currencies). */
export function extractedLineTotal(line: Pick<ExtractedLine, 'quantity' | 'unitPrice'>, decimals: number): number {
  const factor = 10 ** decimals
  return Math.round((line.quantity || 0) * (line.unitPrice || 0) * factor) / factor
}

/**
 * Recomputes the header totals from the current lines: `net` = Σ line totals, `total` = net + tax.
 * The tax stays as the user (or the AI) set it — we don't infer a rate. Mutates the passed object
 * so the review panel updates live.
 */
export function recomputeExtractedTotals(data: ExtractedData, decimals: number): void {
  data.lines.forEach((l) => { l.total = extractedLineTotal(l, decimals) })
  data.net = data.lines.reduce((acc, l) => acc + (l.total || 0), 0)
  data.total = data.net + (data.tax || 0)
}

/** Deep clone so the carousel edits a working copy and only commits it on Confirmar. */
export function cloneExtracted(data: ExtractedData): ExtractedData {
  return { ...data, lines: data.lines.map((l) => ({ ...l })) }
}

/**
 * Whether the working copy diverges from the original extraction. Drives the "edited" badge and the
 * unsaved-changes guard in the review carousel. A structural (JSON) compare is enough here: both
 * objects share the same shape and key order (the working copy is a `cloneExtracted` of the original).
 */
export function isExtractedDirty(original: ExtractedData, working: ExtractedData): boolean {
  return JSON.stringify(original) !== JSON.stringify(working)
}

/**
 * Presentation metadata per consolidated status — color + MDI icon + i18n label key. Phase-2 colors
 * mirror the app's invoice palette (`src/utils/invoiceStatusConfig.ts`) so a file's status reads the
 * same here as on the invoice itself; Phase-1 colors use theme tokens.
 */
export const STATUS_META: Record<MyInvoiceStatus, { color: string; icon: string; labelKey: string }> = {
  PROCESSING: { color: 'orange', icon: 'mdi-cog-sync-outline', labelKey: 'myInvoices.status.processing' },
  FAILED: { color: 'error', icon: 'mdi-alert-circle-outline', labelKey: 'myInvoices.status.failed' },
  NOT_SENT: { color: '#607D8B', icon: 'mdi-tray-full', labelKey: 'myInvoices.status.notSent' },
  IN_REVIEW: { color: '#2196F3', icon: 'mdi-eye-outline', labelKey: 'myInvoices.status.inReview' },
  APPROVED: { color: '#4CAF50', icon: 'mdi-check-circle-outline', labelKey: 'myInvoices.status.approved' },
  REJECTED: { color: '#B00020', icon: 'mdi-close-circle-outline', labelKey: 'myInvoices.status.rejected' },
  PAID: { color: 'success', icon: 'mdi-cash-check', labelKey: 'myInvoices.status.paid' },
}

/** Lifecycle order of the consolidated statuses — drives the status filter chips + any status list. */
export const STATUS_ORDER: readonly MyInvoiceStatus[] = [
  'PROCESSING',
  'FAILED',
  'NOT_SENT',
  'IN_REVIEW',
  'APPROVED',
  'REJECTED',
  'PAID',
]

/** Phase-1 statuses: still processing (no invoice/extraction to open in the carousel yet). */
export const PROCESSING_STATUSES: readonly MyInvoiceStatus[] = ['PROCESSING', 'FAILED']

/** A blank extraction to seed manual entry ("Ingresar Manualmente") of a failed file. */
export function buildEmptyExtraction(): ExtractedData {
  return {
    providerName: '',
    providerTaxId: '',
    invoiceNumber: '',
    emissionDate: '',
    currency: 'CLP',
    net: 0,
    tax: 0,
    total: 0,
    lines: [buildEmptyExtractedLine()],
  }
}

/**
 * Statuses the user can still discard from their inbox. Once a file's invoice enters the approval
 * flow or reaches a terminal state (IN_REVIEW / APPROVED / REJECTED / PAID) it's committed and
 * can't be discarded; PROCESSING can't be discarded mid-extraction either.
 */
export const DISCARDABLE_STATUSES: readonly MyInvoiceStatus[] = ['NOT_SENT', 'FAILED']

export function isDiscardable(status: MyInvoiceStatus): boolean {
  return DISCARDABLE_STATUSES.includes(status)
}

/** Whether the document can be opened in the carousel (extraction available to view/correct). */
export function isReviewable(status: MyInvoiceStatus): boolean {
  return !PROCESSING_STATUSES.includes(status)
}
